import { equal } from "dinero.js"

import { ProductGroups } from "../../../../../../modules/merchant/admin/products/productsTypes"
import { toDinero } from "../../../../../../modules/currency/currencyLib"
import { MonetaryAmount } from "../../../../../../modules/currency/currencyTypes"
import { DeliveryTimesField } from "../../form/formTypes"

type PriceCompareOptions = Readonly<{
    priceListIncludeVat: boolean
}>

// Compares by value, not by representation: the order line and the catalog come from different
// endpoints, so the same amount can arrive with a different scale (2500/2 vs 25000/3).
export function moneyEquals(a?: MonetaryAmount, b?: MonetaryAmount): boolean {
    if (!a || !b) return false
    if (a.currency !== b.currency) return false
    // Identical representation is the common case; short-circuiting it also keeps an
    // unsupported currency code (toDinero throws on those) out of the comparison.
    if (a.amount === b.amount && a.scale === b.scale) return true

    return equal(toDinero(a), toDinero(b))
}

type PriceWithVat = Readonly<{
    price?: MonetaryAmount
    priceInclVat?: MonetaryAmount
    priceExclVat?: MonetaryAmount
}>

// The catalog exposes the price in both VAT bases; compare against the one that matches the
// selected price list so the stored line price and the catalog price share the same basis.
function pickComparablePrice(value: PriceWithVat, opts?: PriceCompareOptions): MonetaryAmount | undefined {
    if (opts?.priceListIncludeVat) {
        return value.priceInclVat ?? value.price
    }

    return value.priceExclVat ?? value.price
}

export function buildPriceMap(list?: ProductGroups[], opts?: PriceCompareOptions): Map<number, MonetaryAmount> {
    const map = new Map<number, MonetaryAmount>()
    for (const group of list ?? []) {
        for (const p of group?.products ?? []) {
            const price = pickComparablePrice(p, opts) ?? null
            if (price) {
                map.set(p.id, price)
            }
        }
    }
    return map
}

export type SelectedLine = Readonly<{ id: number; price: MonetaryAmount | undefined }>

export function collectSelectedLines(deliveries?: DeliveryTimesField): SelectedLine[] {
    const out: SelectedLine[] = []
    for (const slot of deliveries?.deliveryTimes ?? []) {
        for (const line of slot?.orderLines ?? []) {
            if (!line?.isExtraService && typeof line?.id === "number" && line?.price) {
                out.push({ id: line.id, price: line.price })
            }
        }
    }
    return out
}

export function hasPriceDiff(priceById: Map<number, MonetaryAmount>, selected: SelectedLine[]): boolean {
    for (const { id, price } of selected) {
        const current = priceById.get(id)
        if (!current) continue
        if (!price) return true
        if (!moneyEquals(price, current)) return true
    }
    return false
}

export function updatePricesInDeliveries(
    deliveries: DeliveryTimesField,
    priceById: Map<number, MonetaryAmount>,
): { updated: DeliveryTimesField; changed: boolean } {
    let changed = false

    const updated = {
        ...deliveries,
        deliveryTimes: deliveries.deliveryTimes.map((slot) => {
            let slotChanged = false
            const updatedOrderLines = (slot.orderLines ?? []).map((line) => {
                if (line?.isExtraService || typeof line?.id !== "number") return line

                const currentPrice = priceById.get(line.id)
                const oldPrice = line.price as MonetaryAmount | undefined
                if (!currentPrice || !oldPrice) return line
                if (moneyEquals(oldPrice, currentPrice)) return line

                slotChanged = true
                changed = true
                return { ...line, price: currentPrice }
            })
            return slotChanged ? { ...slot, orderLines: updatedOrderLines } : slot
        }),
    }

    return { updated, changed }
}

export function getProductQuantityFromDeliveries(deliveries: DeliveryTimesField, productId: number): number {
    if (!deliveries?.activeTime) return 0

    const activeDelivery = deliveries.deliveryTimes.find((d) => d.time === deliveries.activeTime)
    if (!activeDelivery) return 0

    const product = activeDelivery.orderLines.find((item) => item.id === productId && !item.isExtraService)

    return product?.quantity || 0
}
